import {
  getQueryStringParameters,
  initializeGaEnhancedEcommerce,
  trackViewCartEvent
} from 'src/utils/event-tracking'
import { SHOPIFY_CART_ID } from 'src/utils/globalVariables'
import { captureCoupon } from 'src/utils/session-tracking'
import Client from './utils/client'
import {
  CART_LINES_ADD_OPERATION,
  CART_LINES_REMOVE_OPERATION,
  GET_CART_OPERATION,
  CART_LINES_UPDATE_OPERATION,
  CART_CREATE_OPERATION,
  CART_DISCOUNT_CODE_UPDATE_OPERATION
} from './utils/storefrontClientGraphQL'

import {
  CartDiscountCodesUpdateMutation,
  CartDiscountCodesUpdateMutationVariables,
  CartLinesAddMutation,
  CartLinesAddMutationVariables,
  CartLinesRemoveMutation,
  CartLinesRemoveMutationVariables,
  CartLinesUpdateMutation,
  CartLinesUpdateMutationVariables,
  CreateCartMutation,
  CreateCartMutationVariables,
  GetCartQuery,
  GetCartQueryVariables
} from './../../types/storefront.generated'
import { CartLineInput, CartLineUpdateInput } from 'types/storefront.types'
import { fireDSPPixelEvent } from 'src/utils/dsp-tracking'
import { CartState } from 'src/store/cartAtom'
import { edgeTagAddToCart } from 'src/utils/edgetag-tracking'
import { rodeoAddToCart } from 'src/utils/rodeo-tracking'
import { appLovingCartAdd } from 'src/utils/applovin-tracking'
import { Cart } from 'src/types/cart'
import { SetterOrUpdater } from 'recoil'

interface AttributeProps {
  key: string
  value: string
}

type AddLineItem = {
  merchandiseId: string
  quantity: number
  sellingPlanId?: string
}

export async function initializeCart(setCartState: SetterOrUpdater<CartState>, location: any) {
  try {
    // Check for an existing cart.
    function persistCartToLocalStorage(cart: Cart) {
      if (isBrowser && cart && localStorage) {
        localStorage.setItem(SHOPIFY_CART_ID, cart.id)
      }
      setCartState((prevState: CartState) => ({ ...prevState, cart, fetching: false }))
    }

    const isBrowser = typeof window !== 'undefined'
    const existingCartId = isBrowser && localStorage ? localStorage.getItem(SHOPIFY_CART_ID) : ''
    const customAttributes: AttributeProps[] = []

    const gaClientId = await initializeGaEnhancedEcommerce()
    if (gaClientId) customAttributes.push({ key: 'google-clientID', value: gaClientId })

    const coupon = captureCoupon(location.search) as string
    if (coupon) customAttributes.push({ key: 'coupon', value: coupon })

    const queryString = getQueryStringParameters()
    if (queryString) {
      const params = Object.keys(queryString)
      const formattedAttributes: AttributeProps[] = params.map((param) => ({
        key: param,
        value: String(queryString[param])
      }))
      formattedAttributes.forEach((attr) => customAttributes.push(attr))
    }

    const createNewCart = async () => {
      const cartInput: Record<string, any> = {}
      if (customAttributes.length) {
        cartInput['attributes'] = customAttributes
      }
      const variables: CreateCartMutationVariables = {
        cartInput
      }
      const { data, errors } = await Client.request<CreateCartMutation>(CART_CREATE_OPERATION, {
        variables
      })
      if (errors) {
        console.error('createNewCart', errors)
        Sentry.captureException(errors)
      }
      if (!data?.cartCreate?.cart) {
        throw new Error('Failed to create cart')
      }
      return data.cartCreate.cart
    }

    async function fetchCart(id: string) {
      const variables: GetCartQueryVariables = {
        id
      }
      const { data, errors } = await Client.request<GetCartQuery>(GET_CART_OPERATION, {
        variables
      })
      if (errors) {
        throw errors
      }
      return data?.cart!
    }

    if (existingCartId) {
      try {
        const cart = await fetchCart(existingCartId)
        // If we can fetch cart, that means it hasn't been purchased yet
        persistCartToLocalStorage(cart)
        return
      } catch (e) {
        localStorage.setItem(SHOPIFY_CART_ID, '')
        Sentry.captureException(e)
      }
    }

    const newCart = await createNewCart()
    persistCartToLocalStorage(newCart)
  } catch (err) {
    console.error('initializeCart', err)
    Sentry.captureException(err)
  }
}

interface SubscriptionInterface {
  sellingPlans: Array<{
    id: string
    frequency: string
  }>
  price: number
  compareAtPrice: number
  priceDiscount: number
  percentageDiscount: string
}

interface AddToCartDetailProps {
  productMap: any
  variantId: string
  quantity: number
  frequency: string | number | null
  cartState: CartState
  setCartState: SetterOrUpdater<CartState>
  subscription: SubscriptionInterface | null
  actualTotal: number
  unitPrice: number
}

export const addVariantToCart = async (addToCartDetail: AddToCartDetailProps) => {
  try {
    const {
      productMap,
      variantId,
      quantity,
      frequency,
      cartState,
      setCartState,
      subscription,
      actualTotal,
      unitPrice
    } = addToCartDetail

    fireDSPPixelEvent('AddToShoppingCart')
    edgeTagAddToCart(productMap, variantId, quantity, unitPrice, cartState.cart.checkoutUrl)
    rodeoAddToCart(productMap, variantId, quantity, unitPrice)
    appLovingCartAdd(quantity * unitPrice)

    setCartState((prevState: CartState) => ({ ...prevState, updating: true }))
    const cartId = cartState.cart.id as string

    const lineItem: CartLineInput = {
      merchandiseId: variantId,
      quantity
    }

    // If it's a subscription, attempt to find selling plan ID and add to line item
    if (subscription) {
      const sellingPlan = subscription.sellingPlans.find((plan) => plan.frequency === frequency)
      if (sellingPlan?.id) {
        lineItem['sellingPlanId'] = `gid://shopify/SellingPlan/${sellingPlan.id}`
      }
    }
    const variables: CartLinesAddMutationVariables = {
      cartId,
      lines: [lineItem]
    }
    const { data, errors } = await Client.request<CartLinesAddMutation>(CART_LINES_ADD_OPERATION, {
      variables
    })

    if (errors) {
      throw errors
    }

    if (data?.cartLinesAdd?.cart) {
      const updatedCart = data.cartLinesAdd.cart
      setCartState((prevState: CartState) => ({ ...prevState, cart: updatedCart, updating: false }))
      trackViewCartEvent(
        productMap,
        updatedCart.lines.edges,
        updatedCart.cost.subtotalAmount.amount
      )
    }
  } catch (err) {
    console.error('addVariantToCart ERROR', err)
    Sentry.captureException(err)
  }
}

export const clearCart = async (cart: Cart, setCartState: SetterOrUpdater<CartState>) => {
  const allIds = cart.lines.edges.map((line: any) => line.node.id)
  if (!allIds.length) return cart
  const variables: CartLinesRemoveMutationVariables = {
    cartId: cart.id,
    lineIds: allIds
  }
  const { data, errors } = await Client.request<CartLinesRemoveMutation>(
    CART_LINES_REMOVE_OPERATION,
    { variables }
  )
  if (errors) {
    throw errors
  }
  if (data?.cartLinesRemove?.cart) {
    const updatedCart = data.cartLinesRemove.cart
    setCartState((prevState: CartState) => ({ ...prevState, cart: updatedCart }))
    return updatedCart
  }
}

export const removeVariantFromCart = async (
  cart: Cart,
  productMap: any,
  lineItemID: string,
  setCartState: SetterOrUpdater<CartState>,
  actualTotal: number
) => {
  try {
    const cartId = cart.id
    setCartState((prevState: CartState) => ({ ...prevState, updating: true }))
    const variables: CartLinesRemoveMutationVariables = {
      cartId,
      lineIds: [lineItemID]
    }

    const { data, errors } = await Client.request<CartLinesRemoveMutation>(
      CART_LINES_REMOVE_OPERATION,
      {
        variables
      }
    )

    if (errors) {
      throw errors
    }
    if (data?.cartLinesRemove?.cart) {
      const updatedCart = data.cartLinesRemove.cart
      setCartState((prevState: CartState) => ({ ...prevState, cart: updatedCart, updating: false }))
    }
  } catch (err) {
    console.error('removeVariantFromCart', err)
    Sentry.captureException(err)
  }
}

// only adds if existing SKU + frequency is not already in the cart
export const smartBulkAddVariantsToCart = async ({
  setCartState,
  cart,
  items
}: {
  setCartState: SetterOrUpdater<CartState>
  cart: Cart
  items: any
}) => {
  try {
    setCartState((prevState: CartState) => ({ ...prevState, updating: true }))
    await clearCart(cart, setCartState)
    const encodedItems: CartLineInput[] = items.map(
      (item: {
        variantId: string
        quantity: number
        frequency: number
        subscription: SubscriptionInterface
      }) => {
        const { variantId, quantity, frequency, subscription } = item

        const sellingPlan = subscription?.sellingPlans.find(
          (plan) => plan.frequency === String(frequency)
        )
        const encodedItem: AddLineItem = {
          merchandiseId: variantId,
          quantity
        }
        if (frequency > 0 && sellingPlan) {
          encodedItem['sellingPlanId'] = `gid://shopify/SellingPlan/${sellingPlan.id}`
        }

        return encodedItem
      }
    )

    if (encodedItems.length !== 0) {
      const variables: CartLinesAddMutationVariables = {
        cartId: cart.id,
        lines: encodedItems
      }
      const { data, errors } = await Client.request<CartLinesAddMutation>(
        CART_LINES_ADD_OPERATION,
        { variables }
      )
      if (errors) {
        throw errors
      }

      if (data?.cartLinesAdd?.cart) {
        const updatedCart = data.cartLinesAdd.cart
        setCartState((prevState: CartState) => ({
          ...prevState,
          cart: updatedCart,
          updating: false
        }))
        return
      }
    }

    setCartState((prevState: CartState) => ({ ...prevState, updating: false }))
  } catch (e) {
    console.error('smartBulkAddVariantsToCart', e)
    Sentry.captureException(e)
  }
}

export const updateCartLineItem = async (
  cart: Cart,
  productMap: any,
  lineItemID: string,
  quantity: number,
  setCartState: SetterOrUpdater<CartState>,
  actualTotal: number
) => {
  try {
    setCartState((prevState: CartState) => ({ ...prevState, updating: true }))
    const cartId = cart.id

    const updateLineItems = [{ id: lineItemID, quantity }]
    const variables: CartLinesUpdateMutationVariables = {
      cartId,
      lines: updateLineItems
    }

    const { data, errors } = await Client.request<CartLinesUpdateMutation>(
      CART_LINES_UPDATE_OPERATION,
      { variables }
    )

    if (errors) {
      throw errors
    }
    if (data?.cartLinesUpdate?.cart) {
      const updatedCart = data.cartLinesUpdate.cart
      setCartState((prevState: CartState) => ({
        ...prevState,
        cart: updatedCart,
        updating: false
      }))
    }
  } catch (err) {
    console.error('updateCartLineItem', err)
    Sentry.captureException(err)
  }
}

export const convertToSubscription = async (
  nodeItem: any,
  newFrequency: string,
  cartState: any,
  setCartState: SetterOrUpdater<CartState>,
  subscription: any,
  productMap: any
) => {
  try {
    setCartState((prevState: CartState) => ({ ...prevState, updating: true }))
    // Filters for selling plan with frequency match
    const sellingPlan = subscription.sellingPlans.find(
      (plan: any) => plan.frequency === newFrequency
    )
    const updatedItem: CartLineUpdateInput = {
      id: nodeItem.id,
      merchandiseId: nodeItem.merchandise.id
    }
    if (sellingPlan) {
      updatedItem['sellingPlanId'] = `gid://shopify/SellingPlan/${sellingPlan.id}`
    }
    const variables: CartLinesUpdateMutationVariables = {
      cartId: cartState.cart.id,
      lines: [updatedItem]
    }

    const { data, errors } = await Client.request<CartLinesUpdateMutation>(
      CART_LINES_UPDATE_OPERATION,
      { variables }
    )

    if (errors) {
      throw errors
    }
    if (data?.cartLinesUpdate?.cart) {
      const updatedCart = data.cartLinesUpdate.cart
      setCartState((prevState: CartState) => ({
        ...prevState,
        cart: updatedCart,
        updating: false
      }))
      trackViewCartEvent(
        productMap,
        updatedCart.lines.edges,
        updatedCart.cost.subtotalAmount.amount
      )
      return updatedCart
    }
  } catch (err) {
    console.error('convertToSubscription', err)
    Sentry.captureException(err)
  }
}

export const convertToOtp = async (
  nodeItem: any,
  cartState: CartState,
  setCartState: SetterOrUpdater<CartState>
) => {
  try {
    setCartState((prevState: CartState) => ({ ...prevState, updating: true }))

    const updatedLineItem: CartLineUpdateInput = {
      id: nodeItem.id,
      merchandiseId: nodeItem.merchandise.id,
      sellingPlanId: null
    }

    const variables: CartLinesUpdateMutationVariables = {
      cartId: cartState.cart.id,
      lines: [updatedLineItem]
    }

    const { data, errors } = await Client.request<CartLinesUpdateMutation>(
      CART_LINES_UPDATE_OPERATION,
      { variables }
    )

    if (errors) {
      throw errors
    }

    if (data?.cartLinesUpdate?.cart) {
      const updatedCart = data.cartLinesUpdate.cart
      setCartState((prevState: CartState) => ({
        ...prevState,
        cart: updatedCart,
        updating: false
      }))
    }
  } catch (err) {
    console.error('convertToOtp', err)
    Sentry.captureException(err)
  }
}

export const updateSubscriptionFrequency = async (
  nodeItemId: string,
  newFrequency: string,
  cartState: CartState,
  setCartState: SetterOrUpdater<CartState>,
  subscription: SubscriptionInterface
) => {
  try {
    setCartState((prevState: CartState) => ({ ...prevState, updating: true }))

    // Filters for selling plan with frequency match
    const selectedSellingPlan = subscription.sellingPlans.find(
      (plan: any) => plan.frequency === newFrequency
    )

    if (!selectedSellingPlan) {
      throw new Error('No selling plan found for selected frequency')
    }

    const updatedLineItem: CartLineUpdateInput = {
      id: nodeItemId,
      sellingPlanId: `gid://shopify/SellingPlan/${selectedSellingPlan.id}`
    }

    const variables: CartLinesUpdateMutationVariables = {
      cartId: cartState.cart.id,
      lines: [updatedLineItem]
    }

    const { data, errors } = await Client.request<CartLinesUpdateMutation>(
      CART_LINES_UPDATE_OPERATION,
      { variables }
    )

    if (errors) {
      throw errors
    }

    if (data?.cartLinesUpdate?.cart) {
      const updatedCart = data.cartLinesUpdate.cart
      setCartState((prevState: CartState) => ({
        ...prevState,
        cart: updatedCart,
        updating: false
      }))
    }
  } catch (err) {
    console.error('updateSubscriptionFrequency', err)
    Sentry.captureException(err)
  }
}

export const applyPromoCode = async (
  cartState: CartState,
  setCartState: SetterOrUpdater<CartState>,
  promoCode: string
) => {
  try {
    setCartState((prevState: CartState) => ({ ...prevState, updating: true }))

    const variables: CartDiscountCodesUpdateMutationVariables = {
      cartId: cartState.cart.id,
      discountCodes: [promoCode]
    }

    const { data, errors } = await Client.request<CartDiscountCodesUpdateMutation>(
      CART_DISCOUNT_CODE_UPDATE_OPERATION,
      {
        variables
      }
    )

    if (errors) {
      throw errors
    }

    if (data?.cartDiscountCodesUpdate?.cart) {
      const updatedCart = data.cartDiscountCodesUpdate.cart
      setCartState((prevState: CartState) => ({ ...prevState, cart: updatedCart, updating: false }))
      return updatedCart
    }
  } catch (err) {
    console.error('applyPromoCode', err)
    Sentry.captureException(err)
  }
}

export const removePromoCode = async (cartState: any, setCartState: (args: any) => void) => {
  try {
    setCartState((prevState: CartState) => ({ ...prevState, updating: true }))

    const variables: CartDiscountCodesUpdateMutationVariables = {
      cartId: cartState.cart.id,
      discountCodes: []
    }

    const { data, errors } = await Client.request<CartDiscountCodesUpdateMutation>(
      CART_DISCOUNT_CODE_UPDATE_OPERATION,
      {
        variables
      }
    )

    if (errors) {
      throw errors
    }

    if (data?.cartDiscountCodesUpdate?.cart) {
      const updatedCart = data.cartDiscountCodesUpdate.cart
      setCartState((prevState: CartState) => ({ ...prevState, cart: updatedCart, updating: false }))
      return updatedCart
    }
  } catch (err) {
    console.error('removePromoCode', err)
    Sentry.captureException(err)
  }
}
